import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, AlertTriangle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 100;
const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

function daysSince(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
}

function Th({ label, asc, desc, sort, onSort, className = "" }) {
  const active = sort === asc ? "asc" : sort === desc ? "desc" : null;
  return (
    <th
      className={`px-4 py-3 font-semibold cursor-pointer select-none whitespace-nowrap hover:underline ${active ? "text-blue-900" : ""} ${className}`}
      onClick={() => onSort(active === "asc" ? desc : asc)}
      data-testid={`mylinen-sort-${asc}`}
    >
      {label} {active === "asc" ? "▲" : active === "desc" ? "▼" : <span className="text-neutral-300">↕</span>}
    </th>
  );
}

export const MyLinenSection = ({ hotelId }) => {
  const [tab, setTab] = useState("at_hotel");
  const [productFilter, setProductFilter] = useState("");
  const [productCounts, setProductCounts] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [atHotelTotal, setAtHotelTotal] = useState(0);
  const [overdueTotal, setOverdueTotal] = useState(0);
  const [sort, setSort] = useState("scanned_asc");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        page_size: String(PAGE_SIZE),
        sort,
        overdue: tab === "overdue" ? "true" : "false",
      });
      if (hotelId) params.append("hotel_id", hotelId);
      if (productFilter) params.append("product", productFilter);
      const res = await axios.get(`${API}/linen/my-items?${params}`, { headers: getAuthHeader() });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setAtHotelTotal(res.data.at_hotel_total || 0);
      setOverdueTotal(res.data.overdue_total || 0);
      setProductCounts(res.data.product_counts || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setPage(1); setProductFilter(""); /* eslint-disable-next-line */ }, [hotelId, tab]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, sort, hotelId, tab, productFilter]);

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const switchTab = (t) => { setTab(t); setPage(1); };

  return (
    <div className="space-y-4" data-testid="my-linen-section">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border bg-neutral-100 p-1" data-testid="mylinen-subtabs">
          <button
            onClick={() => switchTab("at_hotel")}
            data-testid="mylinen-tab-athotel"
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              tab === "at_hotel" ? "bg-white shadow text-emerald-700" : "text-neutral-500 hover:text-neutral-800"
            }`}
          >
            <CheckCircle2 className="w-4 h-4" /> At Hotel
            <span className={`px-2 py-0.5 rounded-full text-xs ${tab === "at_hotel" ? "bg-emerald-100 text-emerald-700" : "bg-neutral-200 text-neutral-600"}`}>
              {atHotelTotal}
            </span>
          </button>
          <button
            onClick={() => switchTab("overdue")}
            data-testid="mylinen-tab-overdue"
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
              tab === "overdue" ? "bg-white shadow text-red-700" : "text-neutral-500 hover:text-neutral-800"
            }`}
          >
            <AlertTriangle className="w-4 h-4" /> Overdue
            <span className={`px-2 py-0.5 rounded-full text-xs ${tab === "overdue" ? "bg-red-100 text-red-700" : "bg-neutral-200 text-neutral-600"}`}>
              {overdueTotal}
            </span>
          </button>
        </div>
      </div>

      {productCounts.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="mylinen-product-tabs">
          <button
            onClick={() => { setProductFilter(""); setPage(1); }}
            data-testid="mylinen-product-all"
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
              productFilter === "" ? "bg-blue-900 text-white border-blue-900" : "bg-white text-neutral-600 border-neutral-300 hover:border-blue-900"
            }`}
          >
            All <span className="opacity-70">({productCounts.reduce((s, p) => s + p.count, 0)})</span>
          </button>
          {productCounts.map((p) => (
            <button
              key={p.name}
              onClick={() => { setProductFilter(p.name); setPage(1); }}
              data-testid={`mylinen-product-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                productFilter === p.name ? "bg-blue-900 text-white border-blue-900" : "bg-white text-neutral-600 border-neutral-300 hover:border-blue-900"
              }`}
            >
              {p.name} <span className="opacity-70">({p.count})</span>
            </button>
          ))}
        </div>
      )}

      {tab === "overdue" && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex gap-3" data-testid="overdue-disclaimer">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <span className="font-semibold">Overdue is an estimate only.</span> It does not automatically
            incur any charges — but please keep an eye on these items, as they haven't been seen by our
            drivers for longer than expected.
          </p>
        </div>
      )}

      <Card className="shadow-lg border-0">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-neutral-600 border-b">
                <tr>
                  <th className="w-12 px-2 py-3 font-semibold text-right text-neutral-500">#</th>
                  <Th label="Product" asc="product_asc" desc="product_desc" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                  <Th label="Currently at" asc="location_asc" desc="location_desc" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                  <Th label="Delivered at" asc="delivered_asc" desc="delivered_desc" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                  <Th label="Last scanned" asc="scanned_asc" desc="scanned_desc" sort={sort} onSort={(s) => { setSort(s); setPage(1); }} />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
                )}
                {!loading && items.length === 0 && (
                  <tr><td colSpan="5" className="px-4 py-10 text-center text-gray-400">
                    <Package className="w-10 h-10 mx-auto text-gray-200 mb-2" />
                    {tab === "overdue" ? "No overdue linen — all good!" : "No linen at your hotels yet"}
                  </td></tr>
                )}
                {items.map((it, idx) => (
                  <tr key={idx} className={`border-b last:border-0 ${it.is_overdue ? "bg-red-50/40" : ""}`}>
                    <td className="px-2 py-2 text-right text-neutral-400">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                    <td className="px-4 py-2">{it.product_name}</td>
                    <td className="px-4 py-2">{it.hotel_name}</td>
                    <td className="px-4 py-2 font-mono text-xs whitespace-nowrap"
                        title={it.delivered_at ? it.delivered_at.slice(0, 16).replace("T", " ") : "No delivery record"}>
                      {it.delivered_at
                        ? it.delivered_at.slice(0, 10)
                        : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs whitespace-nowrap"
                        title={it.last_dispatched_at ? it.last_dispatched_at.slice(0, 16).replace("T", " ") : "Never scanned"}>
                      {daysSince(it.last_dispatched_at) !== null
                        ? <span className={it.is_overdue ? "text-red-700 font-bold" : ""}>{daysSince(it.last_dispatched_at)}d</span>
                        : <span className="text-neutral-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t text-sm">
              <span className="text-gray-500">Page {page} / {pages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
